import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, X as XIcon, ChevronDown, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Seo } from '../components/Seo';
import { publicApi } from '../lib/api';

const ORDER = ['glambooth', 'photography', 'videography'];

const Packages = () => {
  const [packages, setPackages] = useState([]);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    publicApi.getPackages().then((d) => setPackages(d || [])).catch(() => {});
  }, []);

  const byKey = (k) => packages.find((p) => p.service_key === k || p.category === k);
  const ordered = ORDER.map(byKey).filter(Boolean);

  const comparison = [
    { key: 'glambooth', label: 'Signature Glam Booth', bullets: ['$749', 'Up to 3 hours', 'Interactive booth experience', 'Digital gallery', 'Optional Unlimited Prints'] },
    { key: 'photography', label: 'Event Photography', bullets: ['$749', 'Up to 3 hours', '≈120 edited photographs', 'Lightroom gallery within 14 days'] },
    { key: 'videography', label: 'Event Highlight Videography', bullets: ['$1,299', 'Up to 3 hours', '2–3 min highlight film', '30-sec vertical teaser', 'Google Drive within 21 days'] },
  ];

  return (
    <div className="min-h-screen bg-[#1F1A17]">
      <Seo
        title="Packages & Pricing"
        description="Compare Mayden Films services: Signature Glam Booth ($749), Event Photography ($749) and Event Highlight Videography ($1,299). Request a quote and check availability."
        path="/packages"
      />

      <section className="pt-36 pb-14 bg-gradient-to-br from-[#1F1A17] via-[#2A2420] to-[#1F1A17]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#EADBC8] mb-5">Packages &amp; Pricing</h1>
          <p className="text-base md:text-lg text-[#EADBC8]/70">
            Three ways to capture your event. Request a quote and check availability — your date is confirmed only after your signed agreement and 30% booking fee have been received.
          </p>
        </div>
      </section>

      <section className="py-14 bg-[#1A1612]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-6">
            {comparison.map((c, i) => (
              <Card key={c.key} className={`bg-[#2A2420] ${i === 0 ? 'border-[#C5A880]' : 'border-[#C5A880]/20'}`}>
                <CardContent className="p-6">
                  {i === 0 && (
                    <span className="inline-flex items-center gap-1 bg-[#C5A880] text-[#1F1A17] text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-full mb-3">
                      <Star size={11} fill="currentColor" /> Primary Service
                    </span>
                  )}
                  <h3 className="text-[#EADBC8] text-lg font-bold mb-1">{c.label}</h3>
                  <p className="text-3xl font-bold text-[#C5A880] mb-4">{c.bullets[0]}</p>
                  <ul className="space-y-1.5 mb-5">
                    {c.bullets.slice(1).map((b) => (
                      <li key={b} className="flex items-start text-[#EADBC8]/75 text-sm">
                        <Check size={16} className="text-[#C5A880] mr-2 mt-0.5 flex-shrink-0" />{b}
                      </li>
                    ))}
                  </ul>
                  <button onClick={() => { setOpen(c.key); document.getElementById(`pkg-${c.key}`)?.scrollIntoView({ behavior: 'smooth' }); }}
                    className="text-[#C5A880] text-sm font-medium hover:underline" data-testid={`view-details-${c.key}`}>
                    View Full Details ↓
                  </button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-14 bg-[#1F1A17]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
          {ordered.map((p) => {
            const isOpen = open === p.service_key;
            return (
              <div id={`pkg-${p.service_key}`} key={p.id} className="bg-[#2A2420] border border-[#C5A880]/20 rounded-xl overflow-hidden">
                <button onClick={() => setOpen(isOpen ? null : p.service_key)}
                  className="w-full flex items-center justify-between p-6 text-left" data-testid={`pkg-toggle-${p.service_key}`}>
                  <div>
                    <h2 className="text-xl md:text-2xl font-bold text-[#EADBC8]">{p.name}</h2>
                    <p className="text-[#C5A880] mt-1">{p.price} · {p.duration}{p.suitable_for ? ` · ${p.suitable_for}` : ''}</p>
                  </div>
                  <ChevronDown size={22} className={`text-[#C5A880] transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                </button>
                {isOpen && (
                  <div className="px-6 pb-6 space-y-5">
                    {(p.turnaround || p.delivery) && (
                      <div className="grid sm:grid-cols-2 gap-3 text-sm">
                        {p.turnaround && <div className="bg-[#1F1A17] rounded-lg p-3"><span className="text-[#C5A880] block text-xs mb-1">Turnaround</span><span className="text-[#EADBC8]/80">{p.turnaround}</span></div>}
                        {p.delivery && <div className="bg-[#1F1A17] rounded-lg p-3"><span className="text-[#C5A880] block text-xs mb-1">Delivery</span><span className="text-[#EADBC8]/80">{p.delivery}</span></div>}
                      </div>
                    )}
                    <div>
                      <h4 className="text-[#C5A880] text-xs uppercase tracking-widest mb-2 font-semibold">Includes</h4>
                      <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5">
                        {(p.features || []).map((f) => (
                          <li key={f} className="flex items-start text-[#EADBC8]/80 text-sm"><Check size={15} className="text-[#C5A880] mr-2 mt-0.5 flex-shrink-0" />{f}</li>
                        ))}
                      </ul>
                    </div>
                    {(p.upgrades || []).length > 0 && (
                      <div>
                        <h4 className="text-[#C5A880] text-xs uppercase tracking-widest mb-2 font-semibold">Optional Upgrades</h4>
                        <ul className="divide-y divide-[#C5A880]/10">
                          {p.upgrades.map((u) => (
                            <li key={u.name} className="flex justify-between py-2 text-sm gap-3">
                              <span className="text-[#EADBC8]/80">{u.name}</span>
                              <span className="text-[#C5A880] whitespace-nowrap">{u.price_label}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {(p.exclusions || []).length > 0 && (
                      <div>
                        <h4 className="text-[#EADBC8]/50 text-xs uppercase tracking-widest mb-2 font-semibold">Not included</h4>
                        <ul className="space-y-1">
                          {p.exclusions.map((e) => (
                            <li key={e} className="flex items-start text-[#EADBC8]/50 text-sm"><XIcon size={14} className="mr-2 mt-0.5 flex-shrink-0" />{e}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    <Link to="/quote"><Button className="bg-[#C5A880] hover:bg-[#D4BC95] text-[#1F1A17] font-semibold w-full sm:w-auto px-8" data-testid={`pkg-quote-${p.service_key}`}>Request a Quote &amp; Check Availability</Button></Link>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      <section className="py-14 bg-[#1A1612]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-[#EADBC8] mb-6 text-center">Important booking notes</h2>
          <ul className="space-y-2.5 text-sm text-[#EADBC8]/75">
            {[
              'A signed booking agreement and 30% booking fee are required to reserve an event date.',
              'The remaining balance is due 14 days before the event.',
              'Photography and videography use the same four monthly booking spaces as Glam Booth bookings.',
              'One operator cannot run an attended Glam Booth and shoot photography/videography at the same time — combined services need an additional operator and a custom quote.',
              'Travel fees apply beyond 30km one way from Cranbourne. Tolls, paid parking, external hire and custom equipment are additional.',
              'Weddings and extended concerts are custom-quoted services.',
              'All prices are in Australian dollars.',
            ].map((r) => (
              <li key={r} className="flex items-start"><span className="text-[#C5A880] mr-2">•</span>{r}</li>
            ))}
          </ul>
          <div className="text-center mt-10">
            <Link to="/quote"><Button className="bg-[#C5A880] hover:bg-[#D4BC95] text-[#1F1A17] font-semibold px-10 py-6">Check Your Event Date</Button></Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Packages;
